import { Tooltip, Typography } from '@mui/joy';
import { Box, CircularProgress } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import pdfIcon from './../../../../../../../assets/svg/attachmentPdfIcon.svg';
import AttachmentPopUp from './../../attachmentPopup';

import otherFileTypeIcon from '../../../../../../../assets/svg/otherFileType.svg';
import videoIcon from '../../../../../../../assets/svg/videoIcon.svg';
import { isRESTAPIError } from '../../../../../../../assets/utils/helpers';
import {
  PDF_FILE_TYPE,
  DOC_FILE_TYPES,
  IMAGE_FILE_TYPES,
  VIDEO_FILE_TYPES,
} from '../../../../../../../constants/fileTypeConstants';
import {
  PDF_MIME_TYPE,
  DOC_MIME_TYPE,
  IMAGE_MIME_TYPES,
  VIDEO_MIME_TYPES,
} from '../../../../../../../constants/mimeTypeConstants';
import { useAuth } from '../../../../../../../contexts/auth/authContext';
import { useToastNotificationContext } from '../../../../../../../hooks/global/alert/useToastNotification';
import { AttachmentObjectProps } from '../../../../../../../interfaces/AttachmentObject';
import ErrorType from '../../../../../../../interfaces/ErrorType';
import { RootState } from '../../../../../../../utils/store';
import SnackBarErrorResponse from '../../../../../../common/snackBarErrorResponse';
import { auditLog, downloadFile } from '../../utils/utils';

import { formatDate } from '~/assets/utils/formatDate';
import { Text } from '~/components/ui/text';

const AttachedFileItemDisplay = ({
  gridView = false,
  id,
  url,
  fileType,
  name,
  createdAt,
  uploaderName,
}: AttachmentObjectProps) => {
  const { reportId } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [error, setError] = useState<ErrorType>({
    errorType: false,
    message: '',
  });
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const userAccessRoles = useSelector(
    (state: RootState) => state.reportData.userAccessRoles,
  );

  const { slug } = useAuth();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const addAuditLog = async () => {
    try {
      setError({
        errorType: false,
        message: '',
      });

      if (reportId && companySlug) {
        const res = await auditLog({
          name,
          companySlug,
          reportId,
        });
        return res;
      } else {
        createToastNotification(
          intl.formatMessage({
            defaultMessage: 'Unauthorized to Audit.',
          }),
          'error',
        );
      }
      setIsLoading(false);
    } catch (err) {
      let errorMessage = intl.formatMessage({
        defaultMessage: 'Error happened. We\'re currently fixing it.',
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened. We\'re currently fixing it.',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Failure to process request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'You are unauthorized to update report',
          });
        } else if (`${err.response.status}` === String(StatusCodes.FORBIDDEN)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'You cannot use this request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      setError({
        errorType: true,
        message: errorMessage,
      });
      setIsLoading(false);
    }
  };

  const downloadAttachment = async () => {
    if (userAccessRoles.is_allowed_to_download_attachments) {
      try {
        await downloadFile({
          url,
          name,
        });
        if (reportId) addAuditLog();
        else setIsLoading(false);
        createToastNotification(
          'Attachment downloaded successfully.',
          'success',
        );
      } catch (downloadError: unknown) {
        createToastNotification('Something went wrong', 'error');
        setIsLoading(false);
      }
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'You are not permitted to download any attachments files on this report.',
        }),
        'info',
      );
      return;
    }
  };

  const getIcon = (currfileType: string) => {
    if (IMAGE_FILE_TYPES.includes(currfileType)) return url;
    if (IMAGE_MIME_TYPES.includes(currfileType)) return url;
    if (VIDEO_FILE_TYPES.includes(currfileType)) return videoIcon;
    if (VIDEO_MIME_TYPES.includes(currfileType)) return videoIcon;
    if (DOC_FILE_TYPES.includes(currfileType)) return otherFileTypeIcon;
    if (DOC_MIME_TYPE === currfileType) return otherFileTypeIcon;
    if (PDF_FILE_TYPE === currfileType) return pdfIcon;
    if (PDF_MIME_TYPE === currfileType) return pdfIcon;

    return otherFileTypeIcon;
  };

  const openAttachment = () => {
    setOpenModal(true);
  };

  const handleCloseSnackbar = (reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setError((prevError) => ({ ...prevError, errorType: false }));
  };

  return (
    <>
      {gridView ? (
        <div key={id} className='flex-1 flex-col flex'>
          <SnackBarErrorResponse
            open={error.errorType}
            message={error.message}
            handleCloseSnackbar={handleCloseSnackbar}
            severity={error.severity ? error.severity : 'error'}
          />

          <div className='flex-1'>
            <Tooltip
              title={intl.formatMessage({ defaultMessage: 'View File' })}
            >
              <button
                className='h-28 w-full flex items-center justify-center'
                onClick={openAttachment}
              >
                <img
                  draggable={false}
                  src={getIcon(fileType)}
                  className='h-full w-full object-contain'
                  alt={name}
                />
              </button>
            </Tooltip>
          </div>
          <div>
            <Tooltip title={name}>
              <Typography
                level='title-sm'
                className='whitespace-nowrap truncate min-w-0 text-left'
              >
                {name}
              </Typography>
            </Tooltip>
            <Typography level='body-sm' className='capitalize'>
              {fileType}
            </Typography>
          </div>
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: '20px',
                left: '40%',
                width: '32px',
                height: '32px',
                borderRadius: '10px',
                bgcolor: 'white',
                padding: 0.5,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
        </div>
      ) : (
        <button
          className='relative flex items-center p-2 text-left overflow-hidden w-full hover:bg-gray-100 cursor-pointer group'
          onClick={openAttachment}
        >
          <div className='w-full flex items-center justify-between '>
            <div className='flex items-center gap-4'>
              <div className='justify-center items-center align-middle'>
                <div className='flex justify-center items-center h-full'>
                  <Box
                    component='img'
                    src={getIcon(fileType)}
                    onClick={openAttachment}
                    sx={{
                      width: '35px',
                      height: '35px',
                      objectFit: 'contain',
                      '&:hover': {
                        cursor: 'pointer',
                      },
                    }}
                  />
                </div>
              </div>

              <Text className='text-md text-gray-500'>{name}</Text>
            </div>
            <div className='flex items-center'>
              <Text className='text-md text-gray-500'>
                {createdAt ? formatDate(createdAt) : ''}
              </Text>
              <Text className='text-md text-gray-500 ml-4'>{uploaderName}</Text>
            </div>
          </div>
        </button>
      )}

      {openModal && (
        <AttachmentPopUp
          handleClose={() => setOpenModal(false)}
          download={downloadAttachment}
          fileType={fileType}
          url={url}
          fileName={name}
        />
      )}
    </>
  );
};

export default AttachedFileItemDisplay;
